import { useMemo } from 'react'

import { useTranslation } from 'next-i18next'

import useGetCorrectedCountryCode from '~/curriculum/hooks/useGetCorrectedCountryCode'
import { useGetUserChosenCurriculumCountryCode } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import { isAuthenticated } from '~/auth/slices/auth.slice'

import { useGetFamilyMemberRewardsQuery, useGetRewardsQuery } from '~/redux/apis/reward-api'
import { useAppSelector } from '~/redux/hooks'

import {
  useGetUserRecentCurriculumCountryQuery,
  useGetUserStatsQuery,
  useGetUserTimeQuery,
} from '~/statistics-v2/api/statistics.api'
import {
  combineTopicsStats,
  toAllMathHeaderStats,
  toGradeKey,
  toHeaderStats,
} from '~/statistics-v2/helper/statistics.helper'
import {
  HeaderStats,
  MasteryCertificate,
  MustKnowsProgressCardProps,
  OverallProgressCardProps,
  TopicStats,
  UserStats,
} from '~/statistics-v2/types/statistics.types'
import { useTestPrepPanelStats } from '~/statistics-v2/hooks/useTestPrepPanelStats'
import useTimeSpentReport from '~/dashboard/hooks/useTimeSpentReport'
import useLearningStats from '~/statistics/hooks/useLearningStats'
import { useGamesStatsQuery, useGetUnMappedActivitiesStatsQuery } from '~/redux/apis/user-stats-api'

export function useGetUserStats(selectedGrade: string, skip = false, userId?: number): UserStats {
  const { t } = useTranslation('statisticsV2')
  const recentCurriculumCountry = useGetUserRecentCurriculumCountryQuery({ familyMemberId: userId })
  const timeSpentReport = useTimeSpentReport(userId)
  const currentUserCurriculumCountry = useGetUserChosenCurriculumCountryCode()
  const { data: unMappedActivitiesStats, isLoading: isUnMappedActivitiesStatsLoading } =
    useGetUnMappedActivitiesStatsQuery(userId ? { familyMemberId: userId } : undefined)

  const countryCode = useGetCorrectedCountryCode(
    userId ? recentCurriculumCountry.data?.countryCode || 'us' : currentUserCurriculumCountry,
  )

  const authenticated = useAppSelector(isAuthenticated)
  const { data: FamilyRewardsData } = useGetFamilyMemberRewardsQuery(
    { familyMemberId: userId },
    {
      skip: !authenticated,
      refetchOnMountOrArgChange: 10,
    },
  )

  const { data: currentUserRewardsData } = useGetRewardsQuery(userId ? { userId } : undefined, {
    skip: !authenticated,
    refetchOnMountOrArgChange: 10,
  })

  const isAllGradesSelected = selectedGrade === '0'

  const rewardsData = userId ? FamilyRewardsData : currentUserRewardsData
  const { videoWatchDurationInMs } = useLearningStats({ familyMemberId: userId })

  const userGrade = toGradeKey(countryCode, selectedGrade)

  const { data: userStats, isLoading: isStatsLoading } = useGetUserStatsQuery({ countryCode, userId }, { skip })

  /*
   * Games for the selection being shown: '0' is all math, so it sends no scope and gets every game
   * ever played; a grade sends that grade and sees only what was played on it.
   *
   * Args are scope-only on purpose — `/user-stats/games` ignores a date range, and `GamesStats`
   * sends the same shape, so both callers share one cache entry instead of firing two requests.
   */
  const gamesScope = {
    familyMemberId: userId,
    ...(isAllGradesSelected ? {} : { grade: userGrade }),
  }

  const { data: gamesStats } = useGamesStatsQuery(gamesScope)

  const { data: userTime, isLoading: isTimeLoading } = useGetUserTimeQuery(
    { countryCode, gradeId: userGrade, userId },
    { skip: isAllGradesSelected || skip },
  )

  const testPrepStats = useTestPrepPanelStats(selectedGrade, countryCode, userId)

  const isLoading = isStatsLoading || isTimeLoading || isUnMappedActivitiesStatsLoading || testPrepStats.isLoading

  const stats = useMemo(() => {
    if (!userStats) return null
    return isAllGradesSelected ? userStats.overall : userStats.grades[userGrade]
  }, [isAllGradesSelected, userGrade, userStats])

  const headerStats = useMemo<HeaderStats>(() => {
    /*
     * Both extra sources go through `toHeaderStats` rather than being folded here, so a third one
     * later cannot land in only one of the four figures. Test prep is single-grade only: the
     * all-math selection already contains it via the unmapped-activities bucket, and blending it
     * there would double-count (see `useTestPrepPanelStats`).
     */
    const baseStats = toHeaderStats(stats, userTime, isAllGradesSelected, {
      games: gamesStats?.activityTotals,
      testPrep: isAllGradesSelected ? undefined : testPrepStats,
    })

    if (!isAllGradesSelected) return baseStats

    return toAllMathHeaderStats(
      baseStats,
      stats,
      {
        timeSpent: timeSpentReport.timeSpent || 0,
        totalXP: rewardsData?.xp || 0,
        videoWatchTime: videoWatchDurationInMs,
      },
      {
        attempts: unMappedActivitiesStats?.totalAttempt || 0,
        correct: unMappedActivitiesStats?.totalCorrect || 0,
      },
      gamesStats?.activityTotals,
    )
  }, [
    stats,
    isAllGradesSelected,
    timeSpentReport.timeSpent,
    rewardsData?.xp,
    videoWatchDurationInMs,
    unMappedActivitiesStats,
    userTime,
    gamesStats?.activityTotals,
    testPrepStats,
  ])

  const overAllProgressStatsCardProps = useMemo<OverallProgressCardProps>(() => {
    if (!stats) {
      return {
        totalTopics: 0,
        completedTopics: 0,
        totalSubtopics: 0,
        completedSubtopics: 0,
        totalLevels: 0,
        completedLevels: 0,
      }
    }

    return {
      totalTopics: stats.totalTopics,
      completedTopics: stats.completedTopics,
      totalSubtopics: stats.totalSubtopics,
      completedSubtopics: stats.completedSubtopics,
      totalLevels: stats.totalLevels,
      completedLevels: stats.completedLevels,
    }
  }, [stats])

  const mustKnowsProgressCardProps = useMemo<MustKnowsProgressCardProps>(() => {
    if (!stats) {
      return {
        total: 0,
        completed: 0,
      }
    }

    return {
      total: stats.totalMustKnows || 0,
      completed: stats.completedMustKnows || 0,
    }
  }, [stats])

  const masteryCertificatesProgressProps = useMemo<MasteryCertificate[]>(() => {
    return stats?.MasteryCertificates || []
  }, [stats])

  const combinedTopics = useMemo<TopicStats[]>(() => {
    if (!userStats || isAllGradesSelected) return []
    const gradeDetails = userStats.grades[userGrade]
    if (!gradeDetails) return []
    return combineTopicsStats(gradeDetails)
  }, [isAllGradesSelected, userGrade, userStats])

  const gradeList = useMemo<Record<string, string>>(() => {
    if (!userStats?.gradeListData) return {}
    return { '0': t('allGrades'), ...userStats.gradeListData }
  }, [userStats, t])

  return {
    countryCode,
    gradeList,
    headerStats,
    overAllProgressStatsCardProps,
    mustKnowsProgressCardProps,
    masteryCertificatesProgressProps,
    topicsStats: combinedTopics,
    isLoading,
  }
}
